import { FormEvent, useCallback, useEffect, useState } from "react";
import * as adminRoleService from "../services/adminRoleService";
import type { Permission, Role } from "../types/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { useToast } from "../components/ui/use-toast";

const AdminRolesPage = () => {
  const { toast } = useToast();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [createForm, setCreateForm] = useState({
    name: "",
    label: "",
    description: "",
    permissions: [] as string[],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [roleData, permissionData] = await Promise.all([
        adminRoleService.listRoles(),
        adminRoleService.listPermissions(),
      ]);
      setRoles(roleData);
      setPermissions(permissionData);
    } catch (err) {
      toast({
        title: "Erreur",
        description: (err as string) ?? "Impossible de charger les rôles.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateRole = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const role = await adminRoleService.createRole(createForm);
      setRoles((prev) => [...prev, role as Role]);
      setCreateForm({ name: "", label: "", description: "", permissions: [] });
      toast({
        title: "Rôle créé",
        description: `${role.label} est disponible.`,
        variant: "success",
      });
      await fetchData();
    } catch (err) {
      toast({
        title: "Erreur",
        description: (err as string) ?? "Création du rôle impossible.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTogglePermission = async (role: Role, permissionKey: string) => {
    const current = new Set(role.permissions);
    current.has(permissionKey)
      ? current.delete(permissionKey)
      : current.add(permissionKey);
    const nextPermissions = Array.from(current);
    try {
      const updated = await adminRoleService.updateRolePermissions(
        role.id,
        nextPermissions
      );
      setRoles((prev) =>
        prev.map((r) => (r.id === role.id ? (updated as Role) : r))
      );
      toast({
        title: "Permissions mises à jour",
        description: `${role.label} dispose maintenant de ${nextPermissions.length} permissions.`,
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          (err as string) ?? "Impossible de mettre à jour les permissions.",
        variant: "destructive",
      });
    }
  };

  const handleCreatePermissionToggle = (
    permissionKey: string,
    checked: boolean
  ) => {
    setCreateForm((prev) => {
      const set = new Set(prev.permissions);
      checked ? set.add(permissionKey) : set.delete(permissionKey);
      return { ...prev, permissions: Array.from(set) };
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground dark:text-white">
          Rôles & permissions
        </h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          Configurez des rôles métiers et attribuez leurs accès en un clic.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {roles.map((role) => (
          <article
            key={role.id}
            className="rounded-3xl border border-border bg-card p-5 shadow-lg dark:border-white/10 dark:bg-white/5 dark:shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground dark:text-white">
                  {role.label}
                </h3>
                <p className="text-xs uppercase tracking-wide text-muted-foreground dark:text-white/50">
                  {role.name}
                </p>
              </div>
              <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground dark:bg-white/10 dark:text-white/70">
                {role.permissions.length} droits
              </span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground dark:text-white/70">
              {role.description ?? "Aucune description fournie."}
            </p>
            <div className="mt-4 space-y-2">
              {permissions.map((permission) => (
                <label
                  key={permission.id}
                  className="flex items-center justify-between rounded-2xl border border-border bg-muted/40 px-3 py-2 text-sm text-foreground/90 dark:border-white/10 dark:bg-black/20 dark:text-white/80">
                  <span>{permission.label}</span>
                  <Checkbox
                    checked={role.permissions.includes(permission.key)}
                    onCheckedChange={() =>
                      handleTogglePermission(role, permission.key)
                    }
                    className="h-5 w-5 rounded-lg border-border dark:border-white/30"
                  />
                </label>
              ))}
            </div>
          </article>
        ))}
      </div>

      <form
        onSubmit={handleCreateRole}
        className="space-y-4 rounded-3xl border border-border bg-card p-6 dark:border-white/10 dark:bg-white/5">
        <h3 className="text-lg font-semibold text-foreground dark:text-white">Créer un rôle</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Nom interne</Label>
            <Input
              value={createForm.name}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, name: event.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Label</Label>
            <Input
              value={createForm.label}
              onChange={(event) =>
                setCreateForm((prev) => ({
                  ...prev,
                  label: event.target.value,
                }))
              }
              required
            />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Description</Label>
            <Textarea
              value={createForm.description}
              onChange={(event) =>
                setCreateForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              placeholder="Décrivez l'usage du rôle…"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Permissions</Label>
          <div className="grid gap-2 md:grid-cols-2">
            {permissions.map((permission) => (
              <label
                key={permission.id}
                className="flex items-center justify-between rounded-2xl border border-border bg-muted/40 px-3 py-2 text-sm text-foreground/90 dark:border-white/10 dark:bg-black/20 dark:text-white/80">
                <span>{permission.label}</span>
                <Checkbox
                  checked={createForm.permissions.includes(permission.key)}
                  onCheckedChange={(checked) =>
                    handleCreatePermissionToggle(
                      permission.key,
                      Boolean(checked)
                    )
                  }
                  className="h-5 w-5 rounded-lg border-border dark:border-white/30"
                />
              </label>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Création..." : "Créer le rôle"}
        </Button>
      </form>
    </div>
  );
};

export default AdminRolesPage;
